import React from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';


import { palette, lightPalette, shape } from '@app/theme';

import { getScoreLevel } from '../analysisReport.utilities';

import { ScoreBreakdownCriteriaTable } from './ScoreBreakdownCriteriaTable';

import type { ScoreBreakdownChartProperties } from './ScoreBreakdownChart.types';
import type { ScoreLevel } from '../analysisReport.utilities';

const LEVEL_COLORS: Record<ScoreLevel, string> = {
  success: palette.success.main,
  warning: palette.warning.main,
  error:   palette.error.main,
};

const LEVEL_BG_COLORS: Record<ScoreLevel, string> = {
  success: palette.success.light,
  warning: palette.warning.light,
  error:   palette.error.light,
};

export const ScoreBreakdownChart: React.FC<ScoreBreakdownChartProperties> = ({ scores, dataTest }) => {
  const overallPercent = scores.max_score > 0
    ? (scores.overall / scores.max_score) * 100
    : 0;
  const overallLevel = getScoreLevel(scores.overall, scores.max_score);
  const overallDisplay = Number.isSafeInteger(scores.overall)
    ? scores.overall.toString()
    : scores.overall.toFixed(1);

  return (
    <Paper
      data-test={dataTest}
      elevation={0}
      sx={{
        border: `1px solid ${lightPalette.border.default}`,
        borderRadius: `${shape.borderRadius}px`,
        overflow: 'hidden',
        backgroundColor: lightPalette.background.paper,
      }}
    >
      {/* Overall score header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${lightPalette.border.default}`,
          backgroundColor: palette.primary.main,
        }}
      >
        {/* Circular progress gauge */}
        <Box sx={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
          {/* Track */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={96}
            thickness={4.5}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              color: 'rgba(255,255,255,0.2)',
            }}
          />
          {/* Fill */}
          <CircularProgress
            variant="determinate"
            value={overallPercent}
            size={96}
            thickness={4.5}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              color: 'primary.contrastText',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
                transition: 'stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1)',
              },
            }}
          />
          {/* Center label */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='span'
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 24,
                fontWeight: 600,
                color: 'primary.contrastText',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {overallDisplay}
            </Box>
          </Box>
        </Box>

        {/* Title block */}
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.65)',
              mb: 0.5,
            }}
          >
            Итоговая оценка
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 28,
              fontWeight: 600,
              color: 'primary.contrastText',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            {overallDisplay}
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 14,
                fontWeight: 400,
                color: 'rgba(255,255,255,0.65)',
                ml: 0.5,
              }}
            >
              / {scores.max_score}
            </Typography>
          </Typography>
          <Box
            sx={{
              display: 'inline-block',
              mt: 1,
              px: 1.5,
              py: 0.25,
              borderRadius: `${shape.borderRadius}px`,
              backgroundColor: LEVEL_BG_COLORS[overallLevel],
              color: LEVEL_COLORS[overallLevel],
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            {overallLevel === 'success' ? 'Высокая' : overallLevel === 'warning' ? 'Средняя' : 'Низкая'}
          </Box>
        </Box>
      </Box>

      {/* Criteria table */}
      {scores.criteria.length > 0 && (
        <ScoreBreakdownCriteriaTable criteria={scores.criteria} />
      )}
    </Paper>
  );
};
